export interface UnhandledRejectionBreadcrumb {
  reasonType: string
  route: string
  appVersion: string
}

export interface WindowErrorBreadcrumb {
  source: 'same-origin' | 'third-party' | 'extension' | 'unknown'
  sourceLocation: 'filename' | 'stack' | 'missing' | 'unusable'
  route: string
  appVersion: string
  kind: 'exception' | 'resource'
  errorName: 'Error' | 'RangeError' | 'ReferenceError' | 'SyntaxError' | 'TypeError' | 'URIError' | 'other' | null
  line?: number
  column?: number
}

const knownErrorNames = new Set([
  'Error',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'TypeError',
  'URIError',
])

/** Keep only a Vite asset pathname; never retain origin, credentials or query. */
export const getDiagnosticAsset = (location: string | undefined, origin: string): string | undefined => {
  if (!location) return undefined

  try {
    const url = new URL(location, origin)
    if (url.origin === origin && !url.username && !url.password
      && /^\/assets\/[\w-]+-[\w-]{8}\.js$/.test(url.pathname)) {
      return url.pathname
    }
  } catch {
    // This is optional context, not error handling; the original event is sent.
  }

  return undefined
}

export const getErrorEventDiagnostics = (
  routePattern: string | undefined,
  appVersion: string,
  entryLocation: string,
  sourceLocations: (string | undefined)[],
  origin: string,
) => ({
  schemaVersion: 1,
  // Callers supply a registered route pattern, never location.pathname/params.
  route: routePattern || 'unmatched',
  appVersion,
  entryAsset: getDiagnosticAsset(entryLocation, origin),
  sourceAsset: sourceLocations.map(location => getDiagnosticAsset(location, origin)).find(Boolean),
})

const getSafeErrorName = (error: unknown): WindowErrorBreadcrumb['errorName'] => {
  if (!(error instanceof Error)) {
    return null
  }

  return knownErrorNames.has(error.name)
    ? error.name as Exclude<WindowErrorBreadcrumb['errorName'], null | 'other'>
    : 'other'
}

const getSource = (location: string | undefined, origin: string): WindowErrorBreadcrumb['source'] => {
  // Some browser integrations report literal sentinel values instead of a
  // script URL. Treating "undefined" as a relative URL would incorrectly
  // attribute an external error to this application.
  if (!location || ['undefined', 'null'].includes(location.trim().toLowerCase())) return 'unknown'

  try {
    const url = new URL(location, origin)
    if (['chrome-extension:', 'moz-extension:', 'safari-web-extension:'].includes(url.protocol)) {
      return 'extension'
    }

    return url.origin === origin ? 'same-origin' : 'third-party'
  } catch {
    return 'unknown'
  }
}

const getStackSource = (error: unknown, origin: string): WindowErrorBreadcrumb['source'] => {
  if (!(error instanceof Error) || typeof error.stack !== 'string') return 'unknown'

  // Classify only a URL scheme from the first stack location. The URL itself
  // can contain a query string, so it must never be included in diagnostics.
  const location = error.stack.match(/(?:https?|chrome-extension|moz-extension|safari-web-extension):\/\/[^\s)]+/)?.[0]
  return getSource(location, origin)
}

/**
 * Describe non-Error promise rejections without serialising their values.
 * Rejection payloads can contain user data, while their type is enough to
 * distinguish application errors from injected or third-party rejections.
 */
export const getUnhandledRejectionBreadcrumb = (
  reason: unknown,
  route: string,
  appVersion: string,
): UnhandledRejectionBreadcrumb | undefined => {
  if (reason instanceof Error) {
    return undefined
  }

  return {
    reasonType: Object.prototype.toString.call(reason),
    route,
    appVersion,
  }
}

export const getWindowErrorBreadcrumb = (
  filename: string | undefined,
  origin: string,
  route: string,
  appVersion: string,
  error: unknown,
  lineNumber: number,
  columnNumber: number,
): WindowErrorBreadcrumb => {
  // Browser extension failures often omit event.filename but preserve a stack
  // location. Fall back to its origin class without serialising that stack.
  const filenameSource = getSource(filename, origin)
  const source = filenameSource === 'unknown'
    ? getStackSource(error, origin)
    : filenameSource
  const sourceLocation: WindowErrorBreadcrumb['sourceLocation'] = filenameSource !== 'unknown'
    ? 'filename'
    : source !== 'unknown'
      ? 'stack'
      : !filename
        ? 'missing'
        : 'unusable'

  const breadcrumb: WindowErrorBreadcrumb = {
    source,
    sourceLocation,
    route,
    appVersion,
    kind: error instanceof Error ? 'exception' : 'resource',
    errorName: getSafeErrorName(error),
  }

  // A same-origin position can be resolved with the release's source map. Do
  // not retain positions for external scripts, where they provide no routeable
  // application diagnosis.
  if (source === 'same-origin' && Number.isFinite(lineNumber)) {
    breadcrumb.line = lineNumber
    if (Number.isFinite(columnNumber)) {
      breadcrumb.column = columnNumber
    }
  }

  return breadcrumb
}
